import { Component, ViewEncapsulation } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * Mobile Bottom Navigation Component
 * Displays main navigation items at the bottom of the screen
 * Shows only first-level items for quick access
 */
@Component({
    selector: 'bottom-navigation',
    standalone: true,
    imports: [RouterModule, MatTabsModule, MatIconModule, MatButtonModule, MatBadgeModule, TranslocoPipe],
    templateUrl: './bottom-navigation.component.html',
    styleUrl: './bottom-navigation.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class BottomNavigationComponent {
    activeItemId: string | null = null;

    /**
     * Constructor
     */
    constructor(private _navigationService: NavigationService) {}

    /**
     * Acceso directo al computed signal de navigation
     */
    navigation = this._navigationService.navigation;

    /**
     * Get only first-level navigation items (excluding dividers and groups)
     */
    getMainNavItems() {
        if (!this.navigation()?.default) {
            return [];
        }

        return this.navigation().default.filter((item) => {
            // Exclude dividers
            if (item.type === 'divider') {
                return false;
            }

            // Only include basic items and groups
            return item.type === 'basic' || item.type === 'group';
        });
    }

    /**
     * Get icon for navigation item
     * Converts outline icons to solid when active
     * @param item Navigation item
     * @param isActive Whether the item is currently active
     */
    getItemIcon(item: any, isActive: boolean = false): string {
        const baseIcon = item.icon || 'heroicons_outline:circle';

        // If active, convert outline to solid
        if (isActive) {
            return baseIcon.replace('heroicons_outline:', 'heroicons_solid:');
        }

        return baseIcon;
    }

    /**
     * Get label for navigation item
     * For groups, show subtitle; for basic items, show title
     */
    getItemLabel(item: any): string {
        return item.type === 'group' ? item.subtitle || item.title : item.title;
    }

    /**
     * Get link for navigation item
     * For groups, use the group link; for basic items, use item link
     */
    getItemLink(item: any): string[] {
        if (item.type === 'group') {
            return [item.link];
        }
        return [item.link];
    }
}
